import { Center, Checkbox, Flex, Spacer, Spinner, Stack, Tag, useColorModeValue } from '@chakra-ui/react';
import { LineItemType, QuoteStatus, VariantType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { getPrettyQuoteStatus, modelUtils, SortNames, toDollars } from '@pelicargo/utils';
import { FreightService } from '@prisma/client';
import { orderBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { LineItemsList } from '../components/LineItemsList';
import { QuoteCard } from '../components/QuoteCard/QuoteCard';
import { QuotesListHeader } from '../components/QuotesListHeader';
import { quoteStatusColorScheme } from '../utils/constants';
import { Quote, Request } from '../utils/types';

type Props = {
  quotes: Quote[];
  request: Request;
  isLoading?: boolean;
  service?: FreightService;
};

const CHEAPEST_ALLOWED_STATUSES: QuoteStatus[] = [
  QuoteStatus.InProgress,
  QuoteStatus.Confirmed,
  QuoteStatus.BookingPending,
  QuoteStatus.BookingRequested,
];
const LINE_ITEMS_LIST_CUTOFF = new Date('2026-03-17T00:00:00.000Z');

const getMinCostFromVariant = (variant: Quote['quote_variants'][0], request?: Request) => {
  const connectedInland = request?.inland_quote_variants?.find?.(
    (qv) => qv.id === variant.inland_service_quote_variant_id,
  );
  const basePrice = connectedInland ? getMinCostFromVariant(connectedInland) : 0;
  return variant.line_items.reduce(
    (currTotalCost: number, currLineItem) => currTotalCost + toDollars(currLineItem.cost),
    basePrice,
  );
};
const calculateMinCost = (quote: Quote, request?: Request) => {
  const generalVariantCosts: number[] =
    quote?.quote_variants
      ?.filter((variant) => variant.type === VariantType.General)
      ?.map((qv) => getMinCostFromVariant(qv, request)) ?? [];

  const otherCosts: number[] =
    quote?.quote_variants
      ?.filter((variant) => variant.type !== VariantType.General)
      ?.map((qv) => getMinCostFromVariant(qv, request)) ?? [];

  const minCost = Math.min(...generalVariantCosts, ...otherCosts);

  if (minCost === Infinity) return null;
  return minCost;
};

export const QuotesList = ({ service, quotes, request, isLoading }: Props) => {
  const [sort, setSort] = useLocalStorage<SortNames>('quote_sorting', 'created_at');
  const [selectedQuoteIds, setSelectedQuoteIds] = useState<number[]>([]);
  const shouldShowLineItemsList = useMemo(
    () =>
      quotes?.some((quote) =>
        quote.quote_variants?.some((variant) => new Date(variant.created_at) > LINE_ITEMS_LIST_CUTOFF),
      ) ?? false,
    [quotes],
  );
  const minCostsByQuoteId = useMemo(
    () =>
      quotes?.reduce(
        (acc, quote) => {
          const minCostQuote = calculateMinCost(quote);
          return { ...acc, [quote.id]: minCostQuote };
        },
        {} as Record<number, number>,
      ),
    [quotes],
  );
  const minTotalCostsByQuoteId = useMemo(
    () =>
      quotes?.reduce(
        (acc, quote) => {
          const minCostQuote = calculateMinCost(quote, request);
          return { ...acc, [quote.id]: minCostQuote };
        },
        {} as Record<number, number>,
      ),
    [quotes, request],
  );

  const overallMinCost = useMemo(
    () =>
      quotes?.reduce((acc, quote) => {
        const minCostQuote = calculateMinCost(quote);

        const hasCheapestAllowedStatus = CHEAPEST_ALLOWED_STATUSES.includes(quote.status);

        if (!minCostQuote || !hasCheapestAllowedStatus) return acc;
        return Math.min(acc, minCostQuote);
      }, Infinity),
    [quotes],
  );

  const handleSelectQuote = useCallback(
    (quoteId: number) => {
      const newSelectedQuoteIds = new Set(selectedQuoteIds);
      if (newSelectedQuoteIds.has(quoteId)) {
        newSelectedQuoteIds.delete(quoteId);
      } else {
        newSelectedQuoteIds.add(quoteId);
      }
      setSelectedQuoteIds(Array.from(newSelectedQuoteIds));
    },
    [selectedQuoteIds],
  );

  const sortQuotes = useCallback(
    (quotes: Quote[]) => {
      switch (sort) {
        case 'origin':
          return orderBy(quotes, 'origin_airport_id');

        case 'destination':
          return orderBy(quotes, 'destination_airport_id');

        case 'cost':
          return orderBy(quotes, (quote) => minCostsByQuoteId[quote.id] ?? Infinity);

        case 'cost_total':
          return orderBy(quotes, (quote) => minTotalCostsByQuoteId[quote.id] ?? Infinity);

        case 'provider_id':
          return orderBy(quotes, 'provider_id');

        case 'created_at':
        default:
          return orderBy(quotes, 'created_at', 'desc');
      }
    },
    [sort, minCostsByQuoteId, minTotalCostsByQuoteId],
  );

  const sortedQuotes = useMemo(() => {
    return sortQuotes(quotes);
  }, [sortQuotes, quotes]);

  const groupedQuotes = useMemo(() => {
    return sortedQuotes?.reduce(
      (acc, quote) => {
        const key = quote.status as QuoteStatus;
        return { ...acc, [key]: [...(acc[key] ?? []), quote] };
      },
      {
        [QuoteStatus.Confirmed]: [],
        [QuoteStatus.BookingPending]: [],
        [QuoteStatus.BookingRequested]: [],
        [QuoteStatus.ReadyForQC]: [],
        [QuoteStatus.Pending]: [],
        [QuoteStatus.SentToCarrier]: [],
        [QuoteStatus.InProgress]: [],
        [QuoteStatus.Expired]: [],
      } as Record<QuoteStatus, Quote[]>,
    );
  }, [sortedQuotes]);

  const someSelected = quotes.some((quote) => selectedQuoteIds.includes(quote.id));
  const handleSelectAllQuotes = useCallback(() => {
    if (someSelected) {
      setSelectedQuoteIds([]);
    } else {
      setSelectedQuoteIds(quotes?.map((quote) => quote.id) ?? []);
    }
  }, [quotes, someSelected]);

  const handleSelectQuotesByStatus = useCallback(
    (status: QuoteStatus) => {
      const quotesByStatus = quotes.filter((quote) => quote.status === status).map((quote) => quote.id);
      const someSelected = quotesByStatus.some((quoteId) => selectedQuoteIds.includes(quoteId));
      const otherSelectedQuotes = quotes.filter(
        (quote) => quote.status !== status && selectedQuoteIds.includes(quote.id),
      );

      if (someSelected) {
        setSelectedQuoteIds(otherSelectedQuotes.map((quote) => quote.id));
        return;
      }

      setSelectedQuoteIds([...selectedQuoteIds, ...quotesByStatus]);
    },
    [quotes, selectedQuoteIds],
  );

  const renderQuote = useCallback(
    (quote: Quote) => {
      const key = `quote-${quote?.id}`;
      const minCost = minCostsByQuoteId?.[quote?.id];
      const minTotalCost = minTotalCostsByQuoteId?.[quote?.id];
      const isCheapest = minCost === overallMinCost && CHEAPEST_ALLOWED_STATUSES.includes(quote.status);
      const isSpotRate = quote?.quote_variants?.some((variant) =>
        variant.line_items.some((item) => item.type === LineItemType.SPOT),
      );
      const isPivotWeight = modelUtils.quote.hasPivotWeight(quote, request);

      return (
        <QuoteCard
          key={key}
          quote={quote}
          minCost={minCost}
          minTotalCost={minTotalCost}
          isCheapest={isCheapest}
          isSpotRate={isSpotRate}
          isPivotWeight={isPivotWeight}
          isSelected={selectedQuoteIds.includes(quote?.id)}
          handleSelectQuote={handleSelectQuote}
        />
      );
    },
    [
      handleSelectQuote,
      minCostsByQuoteId,
      minTotalCostsByQuoteId,
      overallMinCost,
      request?.chargeable_weight,
      selectedQuoteIds,
    ],
  );

  const evenBgColor = useColorModeValue('gray.50', 'gray.800');
  const oddBgColor = useColorModeValue('gray.100', 'gray.900');
  const borderColor = useColorModeValue('gray.300', '');

  const renderQuoteGroup = useCallback(
    (status: QuoteStatus, index: number) => {
      const quotes = groupedQuotes[status as QuoteStatus];
      const sortedQuotes = sortQuotes(quotes);

      const allSelected = sortedQuotes.every((quote) => selectedQuoteIds.includes(quote.id));
      const someSelected = sortedQuotes.some((quote) => selectedQuoteIds.includes(quote.id)) && !allSelected;

      const bgColor = index % 2 === 0 ? evenBgColor : oddBgColor;
      const statusColorScheme = quoteStatusColorScheme[status];

      return (
        <Stack key={status} bg={bgColor} pb={12} spacing={0}>
          <Stack
            direction="row"
            spacing={2}
            align="center"
            pl="8"
            position="sticky"
            top="-0.5"
            py="2"
            zIndex={1}
            borderTopWidth={1}
            borderTopColor={bgColor}
            bg={bgColor}
          >
            <Checkbox
              isIndeterminate={someSelected}
              isChecked={allSelected}
              onChange={() => handleSelectQuotesByStatus(status as QuoteStatus)}
              w="max-content"
              borderColor={borderColor}
            />
            <Stack direction="row" spacing={2} alignItems="center" pl="2">
              <Tag variant="outline" colorScheme={statusColorScheme} fontWeight="500" size="md" w="fit-content">
                {getPrettyQuoteStatus(status as QuoteStatus)} ({sortedQuotes.length})
              </Tag>
            </Stack>
          </Stack>
          <Stack spacing="2" px="4">
            {sortedQuotes.map(renderQuote)}
          </Stack>
        </Stack>
      );
    },
    [
      borderColor,
      evenBgColor,
      groupedQuotes,
      handleSelectQuotesByStatus,
      oddBgColor,
      renderQuote,
      selectedQuoteIds,
      sortQuotes,
    ],
  );

  return (
    <Flex flex={1} direction="column" h="100vh" minW="xl" bgColor="bg.subtle">
      <QuotesListHeader
        request={request}
        quotes={quotes}
        sort={sort}
        setSort={setSort}
        freightService={service}
        selectedQuoteIds={selectedQuoteIds}
        setSelectedQuoteIds={setSelectedQuoteIds}
        handleSelectAllQuotes={handleSelectAllQuotes}
      />
      <Flex flex={1} overflowY="auto">
        <Stack py={4} pt={0} spacing="0" w="full" h="full">
          <Show if={isLoading}>
            <Center h="full" w="full">
              <Spinner />
            </Center>
          </Show>
          <Show if={!isLoading && !quotes?.length}>
            <Center h="full" w="full">
              No quotes found
            </Center>
          </Show>
          <Show if={quotes?.length > 0 && shouldShowLineItemsList}>
            <Stack p={4} pt={0} spacing={0}>
              <LineItemsList requestId={request.id} />
            </Stack>
          </Show>
          <Show if={!isLoading && !!quotes?.length}>
            {Object.keys(groupedQuotes)
              .filter((status) => !!groupedQuotes[status as QuoteStatus].length)
              .map(renderQuoteGroup)}
          </Show>
          <Spacer minH={1} />
        </Stack>
      </Flex>
    </Flex>
  );
};
